/*
 * GeoPickerModal — visuelle Lat/Lon-Auswahl auf einer MapLibre-Karte.
 * Wird von PlantEditModal + CreatePlantModal geöffnet, wenn der Operator
 * Koordinaten nicht als Text eintippen, sondern auf der Karte setzen will.
 *
 * Bedienung:
 *   - Klick auf die Karte → Marker springt an den Klickpunkt.
 *   - Marker per Drag verschieben für Feintuning.
 *   - Optional: Adresssuche via Nominatim (OSM) — Type + Enter → Map fliegt an
 *     das erste Match, Marker wird gesetzt.
 *   - "Übernehmen" schreibt Lat/Lon zurück in den aufrufenden Dialog.
 *
 * Kartenstyle folgt dem Theme-Signal, Tiles kommen vom eigenen pmtiles-Server
 * (tiles.hvacloud.at) — kein zusätzlicher Third-Party-Runtime-Call.
 */

import { useEffect, useRef, useState } from 'preact/hooks';
import { createPortal } from 'preact/compat';
import maplibregl from 'maplibre-gl';
import { buildMapStyle } from '../pages/mapStyle';
import { themeSignal } from '../theme/theme';
import { HeroGeoPicker, IconCancel, IconApply } from '../icons/HvacIcons';
import 'maplibre-gl/dist/maplibre-gl.css';
import './hv-modal.css';
import './GeoPickerModal.css';

interface Props {
  /** Initial-Position. Null = Europa-Übersicht, kein Marker bis geklickt wird. */
  initialLat: number | null;
  initialLon: number | null;
  /** Optionale Adresse als Startpunkt für die Suche. Nutzer kann sie überschreiben. */
  initialAddress?: string;
  onPick(lat: number, lon: number, elevationM: number | null): void;
  onClose(): void;
}

// Europa-Zentrum als Fallback wenn keine Anfangsposition gegeben wird.
const EUROPE_CENTER: [number, number] = [10.5, 48.5];
const EUROPE_ZOOM = 4;
const PICKED_ZOOM = 15;

export function GeoPickerModal({
  initialLat,
  initialLon,
  initialAddress,
  onPick,
  onClose,
}: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const markerRef = useRef<maplibregl.Marker | null>(null);

  const [picked, setPicked] = useState<{ lat: number; lon: number } | null>(
    initialLat !== null && initialLon !== null ? { lat: initialLat, lon: initialLon } : null,
  );
  const [addressQuery, setAddressQuery] = useState(initialAddress ?? '');
  const [searchStatus, setSearchStatus] = useState<
    { kind: 'idle' } | { kind: 'searching' } | { kind: 'error'; message: string } | { kind: 'nohit' }
  >({ kind: 'idle' });
  // Live-Elevation-Lookup. Der Server backfillt sowieso via
  // ElevationBackfillHostedService, aber dem Operator hier direkt die
  // Seehöhe zu zeigen ist ein starker Sanity-Check ("nein, das ist nicht
  // am Berg, das ist am Bahnhof unten") beim Setzen der Position.
  const [elevation, setElevation] = useState<
    { kind: 'idle' } | { kind: 'loading' } | { kind: 'ok'; metres: number } | { kind: 'error' }
  >({ kind: 'idle' });

  // ESC schließt den Picker. Kein onClick-außerhalb-Handler auf dem Backdrop:
  // der Nutzer wählt oft in der Karte, ein versehentlicher Klick auf den Rand
  // darf den ganzen Pick nicht verwerfen.
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  // Map einmalig initialisieren, dann Theme-Wechsel via style-set weitergeben.
  useEffect(() => {
    const el = containerRef.current;
    if (!el || mapRef.current) return;

    const startCenter: [number, number] = picked
      ? [picked.lon, picked.lat]
      : EUROPE_CENTER;
    const startZoom = picked ? PICKED_ZOOM : EUROPE_ZOOM;

    const map = new maplibregl.Map({
      container: el,
      style: buildMapStyle(themeSignal.value),
      center: startCenter,
      zoom: startZoom,
      attributionControl: false,
    });
    mapRef.current = map;
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-left');

    if (picked) {
      installMarker(map, picked.lat, picked.lon);
    }

    map.on('click', (e) => {
      const { lng, lat } = e.lngLat;
      installMarker(map, lat, lng);
      setPicked({ lat, lon: lng });
    });

    // Two safety nets so the canvas paints even when the modal shell is
    // still animating in on mount:
    //   1. Explicit resize after a microtask (container height might have
    //      settled by then).
    //   2. ResizeObserver so any later shell reflow (font-load, viewport
    //      change) triggers a re-fit without user interaction.
    const kick = () => map.resize();
    const rafId = requestAnimationFrame(kick);
    const ro = new ResizeObserver(kick);
    ro.observe(el);

    return () => {
      cancelAnimationFrame(rafId);
      ro.disconnect();
      markerRef.current?.remove();
      markerRef.current = null;
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Theme-Wechsel während der Picker offen ist: neuen Style setzen, Marker
  // überlebt (MapLibre re-attached DOM-Marker beim setStyle).
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    map.setStyle(buildMapStyle(themeSignal.value));
  }, [themeSignal.value]);

  function installMarker(map: maplibregl.Map, lat: number, lon: number) {
    if (markerRef.current) {
      markerRef.current.setLngLat([lon, lat]);
      return;
    }
    const marker = new maplibregl.Marker({ draggable: true, color: readAccent() })
      .setLngLat([lon, lat])
      .addTo(map);
    marker.on('dragend', () => {
      const p = marker.getLngLat();
      setPicked({ lat: p.lat, lon: p.lng });
    });
    markerRef.current = marker;
  }

  // Elevation-Lookup mit 400ms Debounce, damit ein Drag-Refinement nicht
  // pro Frame einen Request feuert. Open-Meteo hat kein Auth + kein
  // Rate-Limit-Header, ist aber public — sobald wir den eigenen
  // OpenTopoData-Container in Prod haben, wechseln wir den URL-Präfix.
  useEffect(() => {
    if (!picked) { setElevation({ kind: 'idle' }); return undefined; }
    setElevation({ kind: 'loading' });
    const controller = new AbortController();
    const t = window.setTimeout(async () => {
      try {
        const url = `https://api.open-meteo.com/v1/elevation?latitude=${picked.lat.toFixed(6)}&longitude=${picked.lon.toFixed(6)}`;
        const res = await fetch(url, { signal: controller.signal });
        if (!res.ok) { setElevation({ kind: 'error' }); return; }
        const j = await res.json() as { elevation?: number[] };
        const m = Array.isArray(j.elevation) && Number.isFinite(j.elevation[0])
          ? j.elevation[0] : null;
        if (m !== null) setElevation({ kind: 'ok', metres: m });
        else setElevation({ kind: 'error' });
      } catch (err) {
        if ((err as { name?: string })?.name !== 'AbortError') setElevation({ kind: 'error' });
      }
    }, 400);
    return () => {
      window.clearTimeout(t);
      controller.abort();
    };
  }, [picked?.lat, picked?.lon]);

  async function runAddressSearch() {
    const q = addressQuery.trim();
    if (q.length < 3) {
      setSearchStatus({ kind: 'error', message: 'Bitte mindestens 3 Zeichen eingeben.' });
      return;
    }
    setSearchStatus({ kind: 'searching' });
    try {
      // Nominatim (OSM) ist frei nutzbar mit fair-use Rate-Limit von 1 req/s.
      // Für gelegentliches Operator-Picking passt das. Ein eigener Geocoder
      // wäre die V2-Antwort — dann bleibt der Runtime-Pfad first-party.
      const url = `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(q)}`;
      const res = await fetch(url, {
        headers: { 'Accept-Language': 'de,en' },
      });
      if (!res.ok) {
        setSearchStatus({ kind: 'error', message: `Suche fehlgeschlagen (HTTP ${res.status}).` });
        return;
      }
      const hits = (await res.json()) as Array<{ lat: string; lon: string; display_name: string }>;
      if (!hits.length) {
        setSearchStatus({ kind: 'nohit' });
        return;
      }
      const lat = Number(hits[0].lat);
      const lon = Number(hits[0].lon);
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
        setSearchStatus({ kind: 'error', message: 'Antwort enthält keine gültigen Koordinaten.' });
        return;
      }
      const map = mapRef.current;
      if (map) {
        map.flyTo({ center: [lon, lat], zoom: PICKED_ZOOM, essential: true });
        installMarker(map, lat, lon);
      }
      setPicked({ lat, lon });
      setSearchStatus({ kind: 'idle' });
    } catch (err) {
      setSearchStatus({ kind: 'error', message: (err as Error).message ?? 'Netzwerkfehler.' });
    }
  }

  const apply = () => {
    if (!picked) return;
    // Elevation ist optional — der Server backfillt sie sowieso via
    // ElevationBackfillHostedService sobald Lat/Lon persistiert sind.
    // Der Callback kriegt sie mit, damit ein Aufrufer sie sofort im
    // Formular anzeigen kann (Sanity-Check).
    onPick(picked.lat, picked.lon, elevation.kind === 'ok' ? elevation.metres : null);
    onClose();
  };

  return createPortal(
    <div class="hv-modal-backdrop">
      <div class="hv-modal hv-modal--wide hv-geopicker" onClick={(e) => e.stopPropagation()}>
        <div class="hv-modal__header">
          <h2 class="hv-modal__title" style={{ display: 'inline-flex', alignItems: 'center', gap: '10px' }}>
            <HeroGeoPicker size={64} style={{ color: 'var(--accent)', flex: 'none' }} />
            Position auf der Karte wählen
          </h2>
          <button type="button" class="hv-modal__close" onClick={onClose} aria-label="Schließen">×</button>
        </div>
        <div class="hv-geopicker__toolbar">
          <input
            type="search"
            class="hv-geopicker__search"
            placeholder="Adresse suchen (z.B. Wien Karlsplatz)"
            value={addressQuery}
            onInput={(e) => setAddressQuery((e.currentTarget as HTMLInputElement).value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); runAddressSearch(); } }}
          />
          <button
            type="button"
            class="hv-btn"
            onClick={runAddressSearch}
            disabled={searchStatus.kind === 'searching'}
          >
            {searchStatus.kind === 'searching' ? 'Suche…' : 'Suchen'}
          </button>
          <div class="hv-geopicker__coord">
            {picked
              ? <>
                  <strong>Lat:</strong> {picked.lat.toFixed(6)}
                  {' · '}<strong>Lon:</strong> {picked.lon.toFixed(6)}
                  {' · '}<strong>Seehöhe:</strong>{' '}
                  {elevation.kind === 'ok' && <>{elevation.metres.toFixed(0)} m</>}
                  {elevation.kind === 'loading' && <span class="hv-geopicker__coord-hint">ermittle…</span>}
                  {elevation.kind === 'error' && <span class="hv-geopicker__coord-hint">n/a</span>}
                  {elevation.kind === 'idle' && '—'}
                </>
              : <span class="hv-geopicker__coord-hint">Klick auf die Karte oder Adresse suchen.</span>}
          </div>
        </div>
        {searchStatus.kind === 'error' && (
          <div class="hv-geopicker__msg hv-geopicker__msg--error">{searchStatus.message}</div>
        )}
        {searchStatus.kind === 'nohit' && (
          <div class="hv-geopicker__msg">Keine Treffer für die Suche.</div>
        )}
        <div class="hv-geopicker__map-wrap">
          <div class="hv-geopicker__map" ref={containerRef} />
        </div>
        <div class="hv-modal__footer">
          <div class="hv-modal__footer-spacer" />
          <button type="button" class="hv-btn" onClick={onClose}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><IconCancel size={14} />Abbrechen</span>
          </button>
          <button
            type="button"
            class="hv-btn hv-btn--primary"
            onClick={apply}
            disabled={!picked}
            title={picked ? undefined : 'Erst einen Punkt auf der Karte wählen'}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><IconApply size={14} />Übernehmen</span>
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function readAccent(): string {
  if (typeof getComputedStyle === 'undefined') return '#77A22B';
  const v = getComputedStyle(document.documentElement).getPropertyValue('--accent').trim();
  return v || '#77A22B';
}
